/**
 * The application shell — LV §123, §124, §127-132.
 *
 * The navigation is the prototype's (V006, 16.08.2026) — a fixed sidebar on the left, in
 * its order — because LV §123 says so: "Die bestehende Navigation und visuelle
 * Grundstruktur sollen weiterverwendet werden" (#456):
 *
 *     Dashboard · Anlagenstandorte · Komponentenstruktur · IH-Aufträge · Wartungspläne ·
 *     Inspektionen · Störmeldungen · Kritikalität & Strategie · Revisionen · Ersatzteile ·
 *     Dokumente & Nachweise · DIN/VDI Regelwerk · Konfiguration
 *
 * Where LV §123 names an entry, its wording stands (Kritikalität & Strategie, DIN/VDI
 * Regelwerk; V006 says "Kritikalität & RCM" and "Compliance"). "Störmeldungen" is V006's
 * and LV §49's. V006's "Zielarchitektur" describes the prototype itself and has no
 * screen here. The entries the prototype does not have stand beside their kin —
 * Arbeitspläne after Wartungspläne, Fremdfirmen after the regulations — and the system
 * screens in their own block before Konfiguration, which stays last as in V006.
 */

import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from "react";
import {
  NavLink,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Bell,
  BookMarked,
  CalendarClock,
  ClipboardCheck,
  ClipboardList,
  Circle,
  Contrast as ContrastIcon,
  Factory,
  FileText,
  Gauge,
  HardHat,
  History,
  LayoutDashboard,
  ListChecks,
  Menu,
  Monitor,
  Moon,
  Network,
  Package,
  RefreshCw,
  Search,
  Settings,
  ShieldCheck,
  Sun,
  SunMoon,
  TriangleAlert,
  Users,
  Wrench,
  type LucideIcon,
} from "lucide-react";

import dahDrop from "@/assets/dah-tropfen.png";
import { Alert, Button, Confirm, Loading, TextInput } from "@/components";
import { LabelProvider } from "@/app/labels";
import { SyncIndicator } from "@/app/SyncIndicator";
import { type FeatureName } from "@/app/feature-flags";
import { useFeatureFlags } from "@/app/useFeatureFlags";
import { NARROW_QUERY, useMediaQuery } from "@/app/useMediaQuery";
import {
  CONTRAST_LABEL,
  getContrast,
  getTheme,
  getUser,
  hasPermission,
  nextContrast,
  nextTheme,
  setContrast,
  setTheme,
  subscribe,
  THEME_LABEL,
  type Contrast,
  type Theme,
} from "@/auth/session";
import { fetchAuthConfig, logout } from "@/auth/oidc";
import { useSession } from "@/auth/useSession";
import { notificationService } from "@/services";

/**
 * Routes are lazy-loaded per feature.
 *
 * A technician on a tablet loads the inspection screen and its checklist logic; they do
 * not need the compliance register or the configuration screens in that bundle.
 */
const DashboardPage = lazy(() => import("@/features/dashboard/DashboardPage"));
const SearchPage = lazy(() => import("@/features/search/SearchPage"));
const SitesPage = lazy(() => import("@/features/sites/SitesPage"));
const AssetsPage = lazy(() => import("@/features/assets/AssetsPage"));
const AssetDetailPage = lazy(() => import("@/features/assets/AssetDetailPage"));
const WorkOrdersPage = lazy(() => import("@/features/work-orders/WorkOrdersPage"));
const WorkOrderDetailPage = lazy(
  () => import("@/features/work-orders/WorkOrderDetailPage"),
);
const MaintenancePlansPage = lazy(
  () => import("@/features/maintenance/MaintenancePlansPage"),
);
const WorkPlansPage = lazy(() => import("@/features/maintenance/WorkPlansPage"));
const InspectionsPage = lazy(() => import("@/features/inspections/InspectionsPage"));
const InspectionRunPage = lazy(
  () => import("@/features/inspections/InspectionRunPage"),
);
const FailuresPage = lazy(() => import("@/features/failures/FailuresPage"));
const RiskPage = lazy(() => import("@/features/risk/RiskPage"));
const RevisionsPage = lazy(() => import("@/features/revisions/RevisionsPage"));
const InventoryPage = lazy(() => import("@/features/inventory/InventoryPage"));
const DocumentsPage = lazy(() => import("@/features/documents/DocumentsPage"));
const CompliancePage = lazy(() => import("@/features/compliance/CompliancePage"));
const ContractorsPage = lazy(() => import("@/features/contractors/ContractorsPage"));
const UsersPage = lazy(() => import("@/features/users/UsersPage"));
const ConfigurationPage = lazy(
  () => import("@/features/configuration/ConfigurationPage"),
);
const AuditPage = lazy(() => import("@/features/configuration/AuditPage"));
const DataQualityPage = lazy(() => import("@/features/quality/DataQualityPage"));
const NotificationsPage = lazy(
  () => import("@/features/notifications/NotificationsPage"),
);
const SyncPage = lazy(() => import("@/features/dashboard/SyncPage"));

interface NavEntry {
  to: string;
  label: string;
  /**
   * A drawn icon rather than a Unicode glyph.
   *
   * The glyphs were three duplicated pairs (⚙, ✓ and ◷ each did two different jobs)
   * and five more that are indistinguishable line marks at 13px, so they cost a column
   * of width and carried no information. Lucide is one 1.5px-stroke set on a 24px grid,
   * ISC-licensed, zero dependencies, tree-shaken to the icons actually imported here.
   */
  icon: LucideIcon;
  /** Hidden without this permission — a UI convenience only (LV §134). */
  permission?: string;
  /**
   * Hidden while this feature flag is off (LV §181).
   *
   * Different in kind from `permission`: a permission says *this user* may not, and
   * the entry reappears for a colleague who may; a flag says *this installation* does
   * not offer the function at all. Both are the server's answer, neither is decided
   * here, and the route behind the entry is guarded as well — a hidden link is not a
   * closed door.
   */
  feature?: FeatureName;
}

interface NavGroup {
  label: string | null;
  items: NavEntry[];
}

/**
 * The prototype's list, flat, then the system screens it never had (see the file
 * comment). Two blocks rather than V006's one, because six screens the prototype did not
 * have would otherwise sit in the middle of the customer's own sequence.
 */
const NAVIGATION: NavGroup[] = [
  {
    label: null,
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard },
      {
        to: "/sites",
        label: "Anlagenstandorte",
        icon: Factory,
        permission: "site.read",
      },
      {
        to: "/assets",
        label: "Komponentenstruktur",
        icon: Network,
        permission: "asset.read",
      },
      {
        to: "/work-orders",
        label: "IH-Aufträge",
        icon: ClipboardList,
        permission: "asset.read",
      },
      {
        to: "/maintenance-plans",
        label: "Wartungspläne",
        icon: CalendarClock,
        permission: "asset.read",
      },
      {
        to: "/work-plans",
        label: "Arbeitspläne",
        icon: ListChecks,
        permission: "asset.read",
      },
      {
        to: "/inspections",
        label: "Inspektionen",
        icon: ClipboardCheck,
        permission: "asset.read",
      },
      {
        to: "/failures",
        label: "Störmeldungen",
        icon: TriangleAlert,
        permission: "asset.read",
      },
      {
        to: "/risk",
        label: "Kritikalität & Strategie",
        icon: Gauge,
        permission: "asset.read",
      },
      { to: "/revisions", label: "Revisionen", icon: Wrench, permission: "asset.read" },
      {
        to: "/inventory",
        label: "Ersatzteile",
        icon: Package,
        permission: "asset.read",
      },
      {
        to: "/documents",
        label: "Dokumente & Nachweise",
        icon: FileText,
        permission: "asset.read",
      },
      {
        to: "/compliance",
        label: "DIN/VDI Regelwerk",
        icon: BookMarked,
        permission: "asset.read",
      },
      {
        to: "/contractors",
        label: "Fremdfirmen",
        icon: HardHat,
        permission: "contractor.read",
      },
    ],
  },
  {
    label: "System",
    items: [
      // No permission: a notification is addressed to one user and the endpoint is
      // keyed on their own id, so there is nothing here to authorise (LV §97).
      { to: "/notifications", label: "Benachrichtigungen", icon: Bell },
      {
        to: "/data-quality",
        label: "Datenqualität",
        icon: ShieldCheck,
        permission: "asset.read",
      },
      {
        to: "/audit",
        label: "Änderungshistorie",
        icon: History,
        permission: "asset.read",
      },
      {
        to: "/users",
        label: "Benutzer & Berechtigungen",
        icon: Users,
        permission: "user.manage",
      },
      {
        to: "/sync",
        label: "Offline & Synchronisation",
        icon: RefreshCw,
        feature: "FEATURE_OFFLINE",
      },
      { to: "/configuration", label: "Konfiguration", icon: Settings },
    ],
  },
];

/** The environment codes the api reports (see `AuthConfig`), as a person says them. */
const ENVIRONMENT_LABEL: Record<string, string> = {
  PROD: "Produktion",
  STAGING: "Abnahme",
  TEST: "Test",
  DEV: "Entwicklung",
  LOCAL: "Lokal",
};

/**
 * The sidebar, as the prototype V006 draws it: brand, the entries, and a footer, on a
 * deep green that does not change with the theme (LV §123, #456). V006's status pill
 * sat under the brand; it is in the header now (see `EnvironmentPill`).
 *
 * One component at every width; only CSS changes its form. From 1101px it is the 280px
 * column; from 641px to 1100px the 88px icon rail, where each entry keeps its name as a
 * `title` and in the accessibility tree; at 640px and below an off-canvas drawer.
 *
 * It used to be the drawer only, and before that a rail that set the document height
 * (decision 084). It does not now: it is `position: sticky` at the full viewport height
 * and scrolls on its own, which is what V006 does too.
 */
function Sidebar({
  open,
  drawer,
  onNavigate,
}: {
  open: boolean;
  drawer: boolean;
  onNavigate: () => void;
}) {
  // The navigation is filtered by permission, so it has to re-render when the permission
  // list arrives. Without this the sidebar rendered once — before `GET /me` resolved — and
  // kept only the entries that need no permission (see `useSession`).
  useSession();
  // Same race, one endpoint later: the shell mounts before
  // `GET /configuration/feature-flags` answers, so a flagged entry has to redraw when it
  // does. Without this the sidebar offered "Offline & Synchronisation" in an
  // installation that had switched offline operation off (LV §181).
  const flags = useFeatureFlags();

  const unread = useQuery({
    queryKey: ["notifications", "unread"],
    queryFn: () => notificationService.unreadCount(),
    refetchInterval: 60_000,
    // A failing badge count must not put an error banner over the whole shell.
    retry: false,
  });

  return (
    <aside
      className={`app__sidebar${open ? " app__sidebar--open" : ""}`}
      id="main-navigation"
      // A closed drawer is off-canvas, and off-canvas is not gone: without `inert` its
      // links stayed in the tab order and the accessibility tree, so Tab walked twenty
      // invisible entries before it reached the page.
      inert={drawer && !open}
    >
      <div className="side__brand">
        {/* The DAH Gruppe drop without its wordmark: "DAH Gruppe" is unreadable at
            this size, and the product name stands right beside it. Decorative — the
            name next to it is what a screen reader announces. */}
        <span className="side__logo" aria-hidden="true">
          <img src={dahDrop} alt="" />
        </span>
        <span className="side__brand-text">
          <b>Maintenance Hub</b>
          <small>Instandhaltungsmanagement</small>
        </span>
      </div>
      <nav className="nav" aria-label="Hauptnavigation">
        {NAVIGATION.map((group) => {
          const visible = group.items.filter(
            (item) =>
              (!item.permission || hasPermission(item.permission)) &&
              (!item.feature || flags[item.feature]),
          );
          if (visible.length === 0) return null;

          return (
            <div key={group.label ?? "root"} className="nav__block">
              {group.label ? <div className="nav__group">{group.label}</div> : null}
              {visible.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  onClick={onNavigate}
                  // The rail shows the icon alone; the tooltip says what it is. The name
                  // itself stays in the label span, visually hidden there, so the link is
                  // never announced as nothing.
                  title={item.label}
                  className={({ isActive }) =>
                    `nav__item${isActive ? " nav__item--active" : ""}`
                  }
                >
                  <item.icon className="nav__icon" aria-hidden="true" />
                  <span className="nav__label">{item.label}</span>
                  {item.to === "/notifications" && (unread.data?.unread ?? 0) > 0 ? (
                    <span className="nav__badge">{unread.data?.unread}</span>
                  ) : null}
                </NavLink>
              ))}
            </div>
          );
        })}
      </nav>
      <div className="side__foot side__text">
        DAH Energie GmbH · Engineering &amp; Project Management
      </div>
    </aside>
  );
}

/**
 * Which installation this is and which build it runs, between the display toggles and
 * the account — where somebody looking at a screenshot or a support call finds it first.
 *
 * The same answer the sign-in screen reads. Inside the application the build and the
 * environment are shown to everybody signed in — the sign-in page hides them on PROD
 * because that page is public (see `LoginFooter`).
 *
 * Theme tokens rather than the sidebar's fixed green, which was chosen for that ground
 * and not for the header's. The text pair (`--text-muted` on `--surface`) is already in
 * contrast.test.ts; the dot is decorative, the words carry the meaning. Up to 1100px the
 * words are visually hidden and stay in the accessibility tree and the tooltip, as they
 * did in the sidebar's rail: the header has no width to spare there.
 */
function EnvironmentPill() {
  const config = useQuery({
    queryKey: ["auth", "config"],
    // `fetchAuthConfig` answers `null` for "could not ask" — offline, a timeout. Thrown
    // rather than cached: with `staleTime: Infinity` a cached `null` kept the pill away
    // for the whole session of a PWA started without a network, and an error is asked
    // again on reconnect (PR 114 review).
    queryFn: async () => {
      const answer = await fetchAuthConfig();
      if (!answer) throw new Error("Umgebung und Version sind gerade nicht abrufbar.");
      return answer;
    },
    staleTime: Infinity,
    retry: false,
  });
  const environment = config.data?.environment;
  const version = config.data?.release_version;
  if (!environment || !version) return null;

  const label = `${ENVIRONMENT_LABEL[environment] ?? environment} · Version ${version}`;
  return (
    <div className="header-env" title={label}>
      <span
        className={`header-env__dot${environment === "PROD" ? " header-env__dot--prod" : ""}`}
        aria-hidden="true"
      />
      <span className="header-env__text">{label}</span>
    </div>
  );
}

/**
 * The entry point for LV §120's global search.
 *
 * In the header rather than in the LV §123 navigation, and deliberately: the search
 * exists for the moment somebody does *not* know which menu entry to press, so putting
 * it behind one of them would miss the case. LV §123 fixes the navigation list, and
 * this adds nothing to it.
 *
 * It submits to `/search?q=…` instead of querying from here. Three things follow from
 * that and all three are wanted: the result is a real address that can be bookmarked
 * and shared, the browser's back button works, and the query survives a reload.
 *
 * No permission guard. Search itself needs none — every object type inside it is
 * filtered per type by the server, and a caller who may read nothing gets an empty
 * result rather than a hidden box (LV §134).
 */
function HeaderSearch() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const active = params.get("q") ?? "";
  const [term, setTerm] = useState(active);
  // Collapsed to its icon until asked for. A permanently open field is the widest thing
  // in the header and is used a fraction of the time the navigation is.
  const [open, setOpen] = useState(false);
  const field = useRef<HTMLInputElement>(null);

  // Follows the address rather than keeping its own idea of the query. Without this the
  // header still read `MAT` after the search had been refined to `IH-2026-000004` on the
  // results screen — two boxes disagreeing about what was being searched for.
  useEffect(() => setTerm(active), [active]);

  return (
    <form
      className={`header-search${open ? " header-search--open" : ""}`}
      role="search"
      // Names the landmark, not the field. The results page carries a second
      // `role="search"`, and two unnamed search landmarks are indistinguishable to a
      // screen reader (LV §193).
      aria-label="Globale Suche"
      onSubmit={(event) => {
        event.preventDefault();
        const query = term.trim();
        // Empty is not "do nothing": below 700 px the field is hidden and this button is
        // the only search affordance in the shell, so pressing it must open the screen
        // that has a field. The minimum query length stays a server rule
        // (`422 SEARCH_QUERY_TOO_SHORT`) and is not repeated here.
        void navigate(query ? `/search?q=${encodeURIComponent(query)}` : "/search");
      }}
    >
      {/* Kept mounted and collapsed by CSS rather than unmounted, so the field holds its
          value while closed and `/search` still carries the same query full width. */}
      <span className="header-search__field">
        <label className="visually-hidden" htmlFor="global-search">
          Suche über alle Objekte
        </label>
        <TextInput
          ref={field}
          id="global-search"
          type="search"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder="Nummer oder Bezeichnung suchen"
          autoComplete="off"
          tabIndex={open ? undefined : -1}
          onKeyDown={(event) => {
            // Escape gives the header back without reaching for the mouse. On the input
            // rather than the form: a form is not an interactive element, and
            // `jsx-a11y/no-noninteractive-element-interactions` is right to say so.
            if (event.key === "Escape") {
              setOpen(false);
              setTerm(active);
            }
          }}
        />
      </span>
      {/* One control, two jobs, and which one it does is never ambiguous: closed it is a
          disclosure that opens the field, open it submits. `type` changes with it so the
          closed state cannot submit an empty form by pressing Enter elsewhere. */}
      <Button
        type={open ? "submit" : "button"}
        variant="ghost"
        size="sm"
        aria-label={open ? "Suchen" : "Suche öffnen"}
        aria-expanded={open}
        aria-controls="global-search"
        onClick={
          open
            ? undefined
            : () => {
                setOpen(true);
                // After paint, or the field is still `visibility: hidden` and refuses focus.
                requestAnimationFrame(() => field.current?.focus());
              }
        }
      >
        <Search className="icon" aria-hidden="true" />
      </Button>
    </form>
  );
}

/**
 * Up to two initials from a display name.
 *
 * Takes the first and last word rather than the first two, so "Sandeep Kumar Jha" gives
 * SJ and not SK — a middle name is the part most likely to be dropped elsewhere, and two
 * people sharing a first name are told apart by the last.
 */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase();
}

function Header({
  onToggleSidebar,
  showMenuButton,
  sidebarOpen,
}: {
  onToggleSidebar: () => void;
  /**
   * The ☰ only exists where it does something — at 640px and below, where the sidebar is
   * a drawer. It used to render at every width while `.app--collapsed` was never applied,
   * so on a desktop it was a control that visibly did nothing — worse than absent,
   * because pressing it taught the user the density was fixed.
   */
  showMenuButton: boolean;
  sidebarOpen: boolean;
}) {
  const [, forceRender] = useState(0);
  const [theme, setThemeState] = useState<Theme>(getTheme);
  const [contrast, setContrastState] = useState<Contrast>(getContrast);
  // Signing out is one click from a control with no label, next to two display toggles.
  // Pressed by accident it costs the whole session — and with the session held in a
  // module variable (Arch §16) there is nothing to come back to but a fresh sign-in.
  const [confirmingLogout, setConfirmingLogout] = useState(false);

  // The session lives outside React (see `auth/session.ts`, which keeps the token out
  // of web storage), so the header subscribes to it rather than reading a hook.
  useEffect(() => subscribe(() => forceRender((n) => n + 1)), []);

  const user = getUser();

  // The cycle order and the German labels live in `auth/session.ts`, because the
  // sign-in screen offers the same two controls and stands outside `App` in the tree.
  const cycleTheme = (): void => {
    const next = nextTheme(theme);
    setTheme(next);
    setThemeState(next);
  };

  // LV §193's "erhöhte Kontrastoptionen". Same three-state cycle as the theme, so the
  // two controls behave identically rather than each needing to be learned — and the
  // same three *distinct* icons, so the state is legible without the tooltip.
  const cycleContrast = (): void => {
    const next = nextContrast(contrast);
    setContrast(next);
    setContrastState(next);
  };

  return (
    <header className="app__header">
      {showMenuButton ? (
        <Button
          variant="ghost"
          onClick={onToggleSidebar}
          aria-label="Navigation ein-/ausblenden"
          aria-expanded={sidebarOpen}
          aria-controls="main-navigation"
        >
          <Menu className="icon" aria-hidden="true" />
        </Button>
      ) : null}
      {/* The product's name and the navigation are in the sidebar now, as in the prototype
          V006; the header keeps the controls its top bar has on the right (#456). */}
      <div className="grow" />
      <HeaderSearch />
      <SyncIndicator />
      <Button
        variant="ghost"
        size="sm"
        onClick={cycleTheme}
        title={`Darstellung: ${THEME_LABEL[theme]}`}
        aria-label="Darstellung wechseln"
      >
        {theme === "dark" ? (
          <Moon className="icon" aria-hidden="true" />
        ) : theme === "light" ? (
          <Sun className="icon" aria-hidden="true" />
        ) : (
          <SunMoon className="icon" aria-hidden="true" />
        )}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={cycleContrast}
        title={`Kontrast: ${CONTRAST_LABEL[contrast]}`}
        aria-label="Kontrast wechseln"
        aria-pressed={contrast === "more"}
      >
        {/* One icon per state, as the theme control already does.
            Three states behind a single mark is what made this confusing: the cycle
            went system -> erhöht -> normal and the button looked identical throughout,
            so the only way to learn where you were was the tooltip.
            "System" is kept rather than dropped, and that is the accessibility call:
            with it gone the app would stamp `normal` on someone whose operating system
            asks for increased contrast, and quietly override an assistive preference
            they set once for everything (LV §193). */}
        {contrast === "more" ? (
          <ContrastIcon className="icon" aria-hidden="true" />
        ) : contrast === "normal" ? (
          <Circle className="icon" aria-hidden="true" />
        ) : (
          <Monitor className="icon" aria-hidden="true" />
        )}
      </Button>
      <EnvironmentPill />
      {user ? (
        <>
          {/**
           * The account, as one fixed-width control.
           *
           * The display name used to sit in the header as text, which made the header's
           * intrinsic width depend on how long someone's name is — the same class of
           * defect that put every page into sideways scroll between 901 and 1100px.
           * "Sandeep Kumar Jha" measured 116px; "Maximilian von Sachsen-Hohenberg" would
           * have measured far more, and nothing in the layout would have said so until a
           * user with a long name signed in. An avatar is 2rem whoever you are.
           *
           * Revealed on hover and on focus, by CSS: a hover region is not an
           * interactive control (`jsx-a11y` refuses mouse handlers on one), and the
           * button carries the name as its accessible label so the panel is never the
           * only way to read it.
           */}
          <div className="header-account">
            <button
              type="button"
              className="header-account__avatar"
              aria-label={`Angemeldet als ${user.user.display_name}`}
              onClick={() => setConfirmingLogout(true)}
            >
              <span aria-hidden="true">{initials(user.user.display_name)}</span>
            </button>
            <div className="header-account__panel">
              <span className="header-account__name">{user.user.display_name}</span>
              <span className="header-account__hint">Zum Abmelden klicken</span>
            </div>
          </div>
          {confirmingLogout ? (
            <Confirm
              title="Abmelden"
              message="Sie werden abgemeldet. Nicht gespeicherte Eingaben in offenen Formularen gehen verloren."
              confirmLabel="Abmelden"
              onConfirm={() => {
                setConfirmingLogout(false);
                void logout();
              }}
              onCancel={() => setConfirmingLogout(false)}
            />
          ) : null}
        </>
      ) : null}
    </header>
  );
}

function Shell() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  // At 640px and below the sidebar is a drawer (see `NARROW_QUERY`); above it the sidebar
  // is on screen and the full-width-or-rail difference is CSS alone.
  const drawer = useMediaQuery(NARROW_QUERY);

  // Close the drawer on navigation. On a phone it covers the content, and leaving it open
  // after a tap is the most common complaint about this pattern.
  useEffect(() => setSidebarOpen(false), [location.pathname]);

  // Escape closes an open drawer, as it closes every other overlay in the application.
  useEffect(() => {
    if (!sidebarOpen) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setSidebarOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sidebarOpen]);

  return (
    <div className={`app${drawer ? " app--drawer" : ""}`}>
      {/* LV §193 — first in the DOM so it is the first tab stop on every page. */}
      <a className="skip-link" href="#main-content">
        Zum Hauptinhalt springen
      </a>
      <Sidebar
        open={sidebarOpen}
        drawer={drawer}
        onNavigate={() => setSidebarOpen(false)}
      />
      {drawer && sidebarOpen ? (
        // The prototype's scrim: a tap beside the open drawer closes it. A button, so it
        // is a real control with a name rather than a click handler on a plain element.
        <button
          type="button"
          className="app__scrim"
          aria-label="Navigation schließen"
          onClick={() => setSidebarOpen(false)}
        />
      ) : null}
      <Header
        onToggleSidebar={() => setSidebarOpen((open) => !open)}
        showMenuButton={drawer}
        sidebarOpen={sidebarOpen}
      />
      {/* `tabIndex={-1}` so the skip link can move focus here and not merely scroll:
          without it the caret stays in the navigation and the next Tab goes back. */}
      <main className="app__main" id="main-content" tabIndex={-1}>
        <Suspense fallback={<Loading rows={6} />}>
          <Outlet />
        </Suspense>
      </main>
    </div>
  );
}

function NotFound() {
  return (
    <Alert tone="warning" title="Seite nicht gefunden">
      Die aufgerufene Adresse existiert nicht.
    </Alert>
  );
}

/**
 * A screen that only exists while its feature flag is on — LV §181.
 *
 * Hiding the navigation entry is not enough: the address is typeable, and a
 * bookmarked `/sync` from a device that ran with offline enabled would otherwise
 * open a screen driven by an engine that is not running.
 *
 * The message names the flag's *function*, in German, rather than the variable: the
 * reader is a technician wondering where their sync page went, and the answer they
 * need is "this installation does not run offline", not `FEATURE_OFFLINE`. Nothing
 * here is a security boundary — every gated action is refused by the backend
 * (LV §134); this is what the user sees instead of a broken screen.
 */
function FeatureRoute({
  feature,
  title,
  children,
}: {
  feature: FeatureName;
  title: string;
  children: ReactNode;
}) {
  const flags = useFeatureFlags();
  if (flags[feature]) return <>{children}</>;
  return (
    <Alert tone="info" title={`${title} ist nicht aktiviert`}>
      Diese Funktion ist in dieser Installation nicht freigeschaltet. Wenden Sie sich an
      die Systemadministration, wenn sie benötigt wird.
    </Alert>
  );
}

// The display preferences are applied in `main.tsx` before the first render, not here:
// an effect in this component cannot reach the sign-in screen, which renders in front
// of it (LV §193, see `applyDisplayPreferences`).
export default function App() {
  return (
    <LabelProvider>
      <Routes>
        <Route element={<Shell />}>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/sites" element={<SitesPage />} />
          <Route path="/assets" element={<AssetsPage />} />
          <Route path="/assets/:assetId" element={<AssetDetailPage />} />
          <Route path="/work-orders" element={<WorkOrdersPage />} />
          <Route path="/work-orders/:orderId" element={<WorkOrderDetailPage />} />
          <Route path="/maintenance-plans" element={<MaintenancePlansPage />} />
          <Route path="/work-plans" element={<WorkPlansPage />} />
          <Route path="/inspections" element={<InspectionsPage />} />
          <Route
            path="/inspections/:inspectionId/run"
            element={<InspectionRunPage />}
          />
          <Route path="/failures" element={<FailuresPage />} />
          <Route path="/risk" element={<RiskPage />} />
          <Route path="/revisions" element={<RevisionsPage />} />
          <Route path="/inventory" element={<InventoryPage />} />
          <Route path="/documents" element={<DocumentsPage />} />
          <Route path="/compliance" element={<CompliancePage />} />
          <Route path="/contractors" element={<ContractorsPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/data-quality" element={<DataQualityPage />} />
          <Route path="/audit" element={<AuditPage />} />
          <Route
            path="/sync"
            element={
              <FeatureRoute
                feature="FEATURE_OFFLINE"
                title="Offline-Betrieb und Synchronisation"
              >
                <SyncPage />
              </FeatureRoute>
            }
          />
          <Route path="/users" element={<UsersPage />} />
          <Route path="/configuration" element={<ConfigurationPage />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </LabelProvider>
  );
}
